import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Building2, Save, Upload, FileText, Image as ImageIcon, Loader2, Eye, Shield, Smartphone, Lock, Unlock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const Section = ({ title, icon: Icon, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
    <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
      <Icon className="w-5 h-5 text-emerald-400" />
      <h2 className="text-lg font-bold text-white">{title}</h2>
    </div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, placeholder, multiline, testId }) => (
  <div>
    <label className="block text-sm font-medium text-gray-400 mb-1.5">{label}</label>
    {multiline ? (
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none resize-y"
        data-testid={testId}
      />
    ) : (
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 outline-none"
        data-testid={testId}
      />
    )}
  </div>
);

const CompanySettingsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/company-settings');
      setSettings(res.data);
    } catch (err) {
      toast.error(t('companySettings.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const updateDocField = (field, value) => {
    setSettings(prev => ({
      ...prev,
      document_settings: { ...prev.document_settings, [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/company-settings', settings);
      setSettings(prev => ({ ...prev, ...res.data }));
      toast.success(t('companySettings.toast.saveSuccess'));
    } catch (err) {
      toast.error(t('companySettings.toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('companySettings.toast.notImage'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('companySettings.toast.tooLarge'));
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/company-settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success(t('companySettings.toast.logoSuccess'));
    } catch (err) {
      toast.error(t('companySettings.toast.logoError'));
    } finally {
      setUploading(false);
    }
  };

  const tabs = [
    { id: 'info', label: t('companySettings.tabs.info'), icon: Building2 },
    { id: 'documents', label: t('companySettings.tabs.documents'), icon: FileText },
    { id: 'payment', label: t('companySettings.tabs.payment'), icon: CreditCard },
    { id: 'logo', label: t('companySettings.tabs.logo'), icon: ImageIcon },
    { id: 'preview', label: t('companySettings.tabs.preview'), icon: Eye },
    { id: 'security', label: t('companySettings.tabs.security'), icon: Shield },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
        </div>
      </Layout>
    );
  }

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="company-settings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white" data-testid="company-settings-title">
              {t('companySettings.title')}
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {t('companySettings.subtitle')}
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('companySettings.save')}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-emerald-600/20 text-emerald-400 border-b-2 border-emerald-400'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'info' && (
          <Section title={t('companySettings.info.sectionTitle')} icon={Building2}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label={t('companySettings.info.companyNameLabel')} value={settings?.company_name} onChange={v => updateField('company_name', v)} placeholder={t('companySettings.info.companyNamePlaceholder')} testId="field-company-name" />
              <Field label={t('companySettings.info.legalFormLabel')} value={settings?.forme_juridique} onChange={v => updateField('forme_juridique', v)} placeholder={t('companySettings.info.legalFormPlaceholder')} testId="field-forme-juridique" />
              <Field label={t('companySettings.info.capitalLabel')} value={settings?.capital_social} onChange={v => updateField('capital_social', v)} placeholder={t('companySettings.info.capitalPlaceholder')} testId="field-capital" />
              <Field label={t('companySettings.info.siretLabel')} value={settings?.siret} onChange={v => updateField('siret', v)} placeholder={t('companySettings.info.siretPlaceholder')} testId="field-siret" />
              <Field label={t('companySettings.info.rcsLabel')} value={settings?.rcs} onChange={v => updateField('rcs', v)} placeholder={t('companySettings.info.rcsPlaceholder')} testId="field-rcs" />
              <Field label={t('companySettings.info.phoneLabel')} value={settings?.phone} onChange={v => updateField('phone', v)} placeholder={t('companySettings.info.phonePlaceholder')} testId="field-phone" />
              <Field label={t('companySettings.info.emailLabel')} value={settings?.email} onChange={v => updateField('email', v)} placeholder={t('companySettings.info.emailPlaceholder')} testId="field-email" />
              <Field label={t('companySettings.info.postalLabel')} value={settings?.postal_code} onChange={v => updateField('postal_code', v)} placeholder={t('companySettings.info.postalPlaceholder')} testId="field-postal" />
            </div>
            <Field label={t('companySettings.info.addressLabel')} value={settings?.address} onChange={v => updateField('address', v)} placeholder={t('companySettings.info.addressPlaceholder')} testId="field-address" />
            <Field label={t('companySettings.info.cityLabel')} value={settings?.city} onChange={v => updateField('city', v)} placeholder={t('companySettings.info.cityPlaceholder')} testId="field-city" />
            <Field label={t('companySettings.info.tvaLabel')} value={settings?.tva_mention} onChange={v => updateField('tva_mention', v)} placeholder={t('companySettings.info.tvaPlaceholder')} testId="field-tva" multiline />
          </Section>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            <Section title={t('companySettings.documents.devisSectionTitle')} icon={FileText}>
              <Field label={t('companySettings.documents.devisValidityLabel')} value={settings?.document_settings?.devis_validite} onChange={v => updateDocField('devis_validite', v)} placeholder={t('companySettings.documents.devisValidityPlaceholder')} testId="field-devis-validite" multiline />
              <Field label={t('companySettings.documents.devisConditionsLabel')} value={settings?.document_settings?.devis_conditions} onChange={v => updateDocField('devis_conditions', v)} placeholder={t('companySettings.documents.devisConditionsPlaceholder')} testId="field-devis-conditions" multiline />
            </Section>

            <Section title={t('companySettings.documents.bcSectionTitle')} icon={FileText}>
              <Field label={t('companySettings.documents.bcConditionsLabel')} value={settings?.document_settings?.bon_commande_conditions} onChange={v => updateDocField('bon_commande_conditions', v)} placeholder={t('companySettings.documents.bcConditionsPlaceholder')} testId="field-bc-conditions" multiline />
            </Section>

            <Section title={t('companySettings.documents.factureSectionTitle')} icon={FileText}>
              <Field label={t('companySettings.documents.facturePaymentLabel')} value={settings?.document_settings?.facture_paiement} onChange={v => updateDocField('facture_paiement', v)} placeholder={t('companySettings.documents.facturePaymentPlaceholder')} testId="field-facture-paiement" multiline />
              <Field label={t('companySettings.documents.facturePenaltiesLabel')} value={settings?.document_settings?.facture_penalites} onChange={v => updateDocField('facture_penalites', v)} placeholder={t('companySettings.documents.facturePenaltiesPlaceholder')} testId="field-facture-penalites" multiline />
              <Field label={t('companySettings.documents.factureRecoveryLabel')} value={settings?.document_settings?.facture_recouvrement} onChange={v => updateDocField('facture_recouvrement', v)} placeholder={t('companySettings.documents.factureRecoveryPlaceholder')} testId="field-facture-recouvrement" multiline />
              <Field label={t('companySettings.documents.factureReserveLabel')} value={settings?.document_settings?.facture_reserve_propriete} onChange={v => updateDocField('facture_reserve_propriete', v)} placeholder={t('companySettings.documents.factureReservePlaceholder')} testId="field-facture-reserve" multiline />
            </Section>

            <Section title={t('companySettings.documents.warrantySectionTitle')} icon={FileText}>
              <Field label={t('companySettings.documents.warrantyLabel')} value={settings?.document_settings?.warranty_text} onChange={v => updateDocField('warranty_text', v)} placeholder={t('companySettings.documents.warrantyPlaceholder')} testId="field-warranty-text" multiline />
              <p className="text-xs text-gray-500">{t('companySettings.documents.warrantyHint')}</p>
            </Section>
          </div>
        )}

        {activeTab === 'payment' && (
          <Section title={t('companySettings.payment.sectionTitle')} icon={CreditCard}>
            <div className="space-y-4">
              <div className="rounded-xl border border-white/10 bg-black/20 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-orange-400" />
                    <div>
                      <h3 className="text-sm font-semibold text-white">{t('companySettings.payment.disableTitle')}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {t('companySettings.payment.disableDesc')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateDocField('disable_online_payment', !settings?.document_settings?.disable_online_payment)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      settings?.document_settings?.disable_online_payment ? 'bg-red-500' : 'bg-gray-600'
                    }`}
                    data-testid="toggle-disable-online-payment"
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                      settings?.document_settings?.disable_online_payment ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                <div className={`mt-3 px-3 py-2 rounded-lg text-xs ${
                  settings?.document_settings?.disable_online_payment
                    ? 'bg-red-500/10 border border-red-500/20 text-red-400'
                    : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                }`}>
                  {settings?.document_settings?.disable_online_payment
                    ? t('companySettings.payment.statusDisabled')
                    : t('companySettings.payment.statusEnabled')}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-5">
                <h3 className="text-sm font-semibold text-white mb-2">{t('companySettings.payment.howTitle')}</h3>
                <div className="space-y-2 text-xs text-gray-400">
                  <p>{t('companySettings.payment.step1')}</p>
                  <p>{t('companySettings.payment.step2')}</p>
                  <p>{t('companySettings.payment.step3')}</p>
                  <p>{t('companySettings.payment.step4')}</p>
                </div>
              </div>
            </div>
          </Section>
        )}

        {activeTab === 'logo' && (
          <Section title={t('companySettings.logo.sectionTitle')} icon={ImageIcon}>
            <p className="text-sm text-gray-400">{t('companySettings.logo.description')}</p>
            <div className="flex flex-col sm:flex-row items-start gap-6 mt-4">
              <div className="w-40 h-40 rounded-xl border-2 border-dashed border-white/20 bg-black/30 flex items-center justify-center overflow-hidden">
                {settings?.logo_url ? (
                  <img src={`${API_BASE}${settings.logo_url}`} alt={t('companySettings.logo.alt')} className="w-full h-full object-contain p-2" data-testid="company-logo-preview" />
                ) : (
                  <div className="text-center text-gray-500">
                    <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                    <span className="text-xs">{t('companySettings.logo.none')}</span>
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <label className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium cursor-pointer transition-colors border border-white/10">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? t('companySettings.logo.uploading') : t('companySettings.logo.choose')}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
                </label>
                <p className="text-xs text-gray-500">{t('companySettings.logo.hint')}</p>
              </div>
            </div>
          </Section>
        )}
        {activeTab === 'preview' && (
          <PdfPreviewTab api={api} />
        )}
        {activeTab === 'security' && (
          <SecurityTab api={api} />
        )}
      </div>
    </Layout>
  );
};

const PdfPreviewTab = ({ api }) => {
  const { t } = useLanguage();
  const [docType, setDocType] = useState('facture');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const handlePreview = async () => {
    setLoading(true);
    setPdfUrl(null);
    try {
      const res = await api.get(`/company-settings/preview-pdf/${docType}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPdfUrl(url);
    } catch (err) {
      toast.error(t('companySettings.toast.previewError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  return (
    <Section title={t('companySettings.preview.sectionTitle')} icon={Eye}>
      <p className="text-sm text-gray-400 mb-4">
        {t('companySettings.preview.description')}
        <br/><span className="text-yellow-500/80 text-xs">{t('companySettings.preview.saveReminder')}</span>
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg overflow-hidden border border-white/10">
          {['facture', 'devis'].map(tp => (
            <button
              key={tp}
              onClick={() => { setDocType(tp); setPdfUrl(null); }}
              className={`px-4 py-2 text-sm font-medium transition-colors ${docType === tp ? 'bg-emerald-600 text-white' : 'bg-black/30 text-gray-400 hover:text-white'}`}
              data-testid={`preview-type-${tp}`}
            >
              {tp === 'facture' ? t('companySettings.preview.facture') : t('companySettings.preview.devis')}
            </button>
          ))}
        </div>
        <button
          onClick={handlePreview}
          disabled={loading}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors disabled:opacity-50"
          data-testid="generate-preview-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          {loading ? t('companySettings.preview.generating') : t('companySettings.preview.generate')}
        </button>
      </div>
      {pdfUrl && (
        <div className="mt-5 rounded-xl overflow-hidden border border-white/10" data-testid="pdf-preview-container">
          <iframe
            src={pdfUrl}
            title={t('companySettings.preview.iframeTitle')}
            className="w-full bg-white"
            style={{ height: '75vh', minHeight: 500 }}
          />
        </div>
      )}
    </Section>
  );
};

const SecurityTab = ({ api }) => {
  const { t } = useLanguage();
  const [twoFAStatus, setTwoFAStatus] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api.get('/auth/2fa/status')
      .then(res => setTwoFAStatus(res.data.enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const handleSetup = async () => {
    setMessage('');
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
    } catch (err) {
      setMessage(err.response?.data?.detail || t('companySettings.security.genericError'));
    }
  };

  const handleVerify = async () => {
    try {
      await api.post('/auth/2fa/verify', { code: verifyCode });
      setTwoFAStatus(true);
      setSetupData(null);
      setVerifyCode('');
      setMessage('');
      toast.success(t('companySettings.security.enabledToast'));
    } catch (err) {
      setMessage(err.response?.data?.detail || t('companySettings.security.invalidCode'));
    }
  };

  const handleDisable = async () => {
    try {
      await api.post('/auth/2fa/disable', { code: disableCode });
      setTwoFAStatus(false);
      setDisableCode('');
      setMessage('');
      toast.success(t('companySettings.security.disabledToast'));
    } catch (err) {
      setMessage(err.response?.data?.detail || t('companySettings.security.invalidCode'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;
  }

  return (
    <Section title={t('companySettings.security.sectionTitle')} icon={Shield}>
      {message && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="security-message">
          {message}
        </div>
      )}

      <div className="rounded-xl border border-white/10 bg-black/20 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-emerald-400" />
            <div>
              <h3 className="text-sm font-semibold text-white">{t('companySettings.security.twoFaTitle')}</h3>
              <p className="text-xs text-gray-500">Google Authenticator / TOTP</p>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${twoFAStatus ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`} data-testid="2fa-status-badge">
            {twoFAStatus ? t('companySettings.security.statusActive') : t('companySettings.security.statusInactive')}
          </span>
        </div>

        {!twoFAStatus && !setupData && (
          <div>
            <p className="text-xs text-gray-400 mb-3">
              {t('companySettings.security.intro')}
            </p>
            <button onClick={handleSetup} data-testid="setup-2fa-btn" className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors">
              <Lock className="w-4 h-4" /> {t('companySettings.security.configureBtn')}
            </button>
          </div>
        )}

        {setupData && (
          <div className="space-y-4 mt-2 bg-black/30 rounded-xl p-4 border border-white/5">
            <p className="text-xs text-gray-400">{t('companySettings.security.step1ScanPrefix')} <strong className="text-white">Google Authenticator</strong>{t('companySettings.security.step1ScanSuffix')}</p>
            {setupData.qr_code && (
              <div className="flex justify-center">
                <img src={setupData.qr_code} alt={t('companySettings.security.qrAlt')} className="w-48 h-48 rounded-lg" data-testid="2fa-qr-code" />
              </div>
            )}
            <div>
              <p className="text-[10px] text-gray-500 mb-1">{t('companySettings.security.manualKeyLabel')}</p>
              <code className="text-xs text-yellow-400 bg-black/40 px-3 py-1.5 rounded block text-center break-all" data-testid="2fa-secret-key">{setupData.secret}</code>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('companySettings.security.step2Enter')}</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  maxLength={6}
                  className="flex-1 px-3 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white text-center tracking-[0.3em] font-mono focus:outline-none focus:border-emerald-500/40"
                  data-testid="2fa-verify-input"
                />
                <button
                  onClick={handleVerify}
                  disabled={verifyCode.length !== 6}
                  data-testid="2fa-verify-btn"
                  className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 disabled:opacity-40 transition-colors"
                >
                  {t('companySettings.security.activate')}
                </button>
              </div>
            </div>
          </div>
        )}

        {twoFAStatus && (
          <div className="mt-2 bg-black/30 rounded-xl p-4 border border-white/5">
            <p className="text-xs text-emerald-400 mb-3 flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> {t('companySettings.security.protectedMsg')}
            </p>
            <p className="text-xs text-gray-500 mb-2">{t('companySettings.security.disablePrompt')}</p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={disableCode}
                onChange={e => setDisableCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                maxLength={6}
                className="flex-1 px-3 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white text-center tracking-[0.3em] font-mono focus:outline-none"
                data-testid="2fa-disable-input"
              />
              <button
                onClick={handleDisable}
                disabled={disableCode.length !== 6}
                data-testid="2fa-disable-btn"
                className="px-4 py-2.5 bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg text-sm font-medium hover:bg-red-500/30 disabled:opacity-40 transition-colors flex items-center gap-1.5"
              >
                <Unlock className="w-4 h-4" /> {t('companySettings.security.disable')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default CompanySettingsPage;
